"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Maximize2, ChevronDown, CreditCard, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { useOrderSidebar } from '@/context/OrderSidebarContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { useSession } from '@/context/SessionContext';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { X, ReceiptText, Loader2 } from '@/components/icons';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useSidebarWidth } from '@/hooks/useSidebarWidth';
import { cn, formatCurrency, getAvatarInitials } from '@/lib/utils';
import { formatMessageWithCurrency } from '@/lib/formatMessageWithCurrency';
import { getOrderDisplayNumber } from '@/lib/utils-order';
import {
  DISPATCH_STATUS_SETTING_OPTIONS,
  STORE_STATUS_SETTING_OPTIONS,
  getDispatchStatusProgress,
  getDispatchStatusLabel,
  getStoreStatusProgress,
  getStoreStatusLabel,
} from '@/lib/orderStatusLabels';
import { supabase, getProductById } from '@/lib/supabase';
import { isUserMember, getStoreById } from '@/lib/supabase/stores';
import { updateOrderStatus } from '@/lib/supabase/orders';
import {
  getActiveAssignmentForOrder,
  releaseOrderAssignment,
  completeOrderAssignment,
  getAssignmentsByOrderId,
} from '@/lib/supabase/order_dispatch_assignments';
import { insertChangaBoard } from '@/lib/supabase/changas_board';
import { AuctionOrderCountdown } from '@/components/AuctionOrderCountdown';
import { PaymentSidebar } from '@/components/PaymentSidebar';
import { AddressPickerSidebar } from '@/components/AddressPickerSidebar';
import { DeliveryTrackingPanel } from '@/components/DeliveryTrackingPanel';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import OrderItemsList from '@/components/OrderItemsList';

const COURIER_LOCATION_MAX_AGE_MS = 10 * 60 * 1000;
const COURIER_MAP_DEBUG = true;

function renderOrderNotes(notes: any) {
  if (!notes) return null;

  let parsed = null;

  if (typeof notes === 'string') {
    try {
      parsed = JSON.parse(notes);
    } catch {
      return <div className="whitespace-pre-line text-sm">{notes}</div>;
    }
  } else if (typeof notes === 'object') {
    parsed = notes;
  }

  if (!parsed || typeof parsed !== 'object') {
    return <div className="whitespace-pre-line text-sm">{notes}</div>;
  }

  if (parsed.type === 'tour_bundle' && Array.isArray(parsed.passengers) && Array.isArray(parsed.tours)) {
    return (
      <div className="space-y-2">
        <div className="text-sm font-semibold text-primary">Reserva de excursión</div>
        <div>
          <span className="font-medium">Pasajeros:</span>
          <ul className="ml-5 list-disc text-sm">
            {parsed.passengers.map((passenger: any, index: number) => (
              <li key={passenger.id || index}>
                {passenger.name} {passenger.documentNumber ? `(DNI: ${passenger.documentNumber})` : ''}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className="font-medium">Excursiones:</span>
          <ul className="ml-5 list-disc text-sm">
            {parsed.tours.map((tour: any, index: number) => (
              <li key={tour.tourid || tour.tourId || index}>
                <span className="font-semibold">{tour.tourTitle || tour.destination || 'Excursión'}</span>
                {tour.date && (
                  <span>
                    {' '}
                    — <span className="text-muted-foreground">{new Date(tour.date).toLocaleDateString()}</span>
                  </span>
                )}
                {tour.destination && <span className="ml-2 text-muted-foreground">({tour.destination})</span>}
              </li>
            ))}
          </ul>
        </div>
        {parsed.totalPrice && <div className="font-bold text-primary">Total: ${parsed.totalPrice}</div>}
      </div>
    );
  }

  return <pre className="overflow-x-auto rounded bg-muted/30 p-2 text-xs">{JSON.stringify(parsed, null, 2)}</pre>;
}

function readCourierStageFromNotes(notes: string | null | undefined): string | null {
  if (!notes) return null;

  try {
    const parsed = JSON.parse(notes);
    const stage = parsed?.stage;
    return typeof stage === 'string' ? stage : null;
  } catch {
    return notes;
  }
}

function hasCourierReachedLocalByNotes(notes: string | null | undefined): boolean {
  if (!notes) return false;

  const raw = String(notes).toLowerCase();
  return (
    raw.includes('picked_up') ||
    raw.includes('arrived_local') ||
    raw.includes('arrive_local') ||
    raw.includes('llegue_local') ||
    raw.includes('retiro_en_local')
  );
}

function parsePointWkt(value: unknown): { lat: number; lng: number } | null {
  if (typeof value !== 'string') return null;

  const match = value.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/i);
  if (!match) return null;

  const lng = Number(match[1]);
  const lat = Number(match[2]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function readCourierCoordsFromProfileRow(row: any): { lat: number; lng: number } | null {
  const lat = Number(row?.relevant_latitude);
  const lng = Number(row?.relevant_longitude);

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng };
  }

  return parsePointWkt(row?.last_location);
}

// Simple extractor: only get essential fields (id, product_id, title, image_url, quantity)
function extractSimpleItem(item: any): any {
  if (!item) return null;

  const extractImageUrl = (img: any): string | null => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (Array.isArray(img) && img.length > 0) return extractImageUrl(img[0]);
    if (typeof img === 'object') {
      const keys = Object.keys(img);
      if (keys.length > 0) return img[keys[0]];
    }
    return null;
  };

  const title =
    item?.product_title ??
    item?.title ??
    item?.products?.title ??
    item?.product?.title ??
    item?.product_name ??
    item?.name;

  const rawImageUrl =
    item?.image_url ??
    item?.images_url ??
    item?.image_urls ??
    item?.products?.image_url ??
    item?.products?.image_urls ??
    item?.product?.image_url ??
    item?.thumbnail ??
    item?.image;

  const image_url = extractImageUrl(rawImageUrl);

  const quantity = Number(item?.quantity ?? item?.qty ?? item?.count ?? 1) || 1;
  const unit_price = Number(item?.unit_price ?? item?.unitPrice ?? item?.price ?? 0) || 0;
  const product_id = item?.product_id ?? item?.product?.id ?? item?.id;

  // Only include if we have at least a title
  if (!title) return null;

  return {
    id: item?.id || `item-${product_id}-${Date.now()}`,
    product_id: product_id,
    title: title,
    image_url: image_url,
    quantity: quantity,
    unit_price: unit_price,
  };
}

function normalizeOrderItems(raw: any): any[] {
  if (!raw) return [];

  // If raw is an array, normalize each element
  if (Array.isArray(raw)) {
    return raw
      .map(extractSimpleItem)
      .filter(Boolean);
  }

  // If raw is a JSON string, try to parse and normalize
  if (typeof raw === 'string') {
    try {
      return normalizeOrderItems(JSON.parse(raw));
    } catch {
      return [];
    }
  }

  // If raw is an object that wraps an items array
  if (typeof raw === 'object') {
    if (Array.isArray(raw.items)) return normalizeOrderItems(raw.items);
    if (Array.isArray(raw.items_list)) return normalizeOrderItems(raw.items_list);
    if (Array.isArray(raw.order_items)) return normalizeOrderItems(raw.order_items);
    return [];
  }

  return [];
}

function BankDataBlock({
  buyerCbu,
  buyerBankName,
  buyerAccountHolder,
  onCopyCbu,
  title = 'Datos de pago',
}: {
  buyerCbu?: string | null;
  buyerBankName?: string | null;
  buyerAccountHolder?: string | null;
  onCopyCbu: () => void;
  title?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isStacked, setIsStacked] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsStacked(entry.contentRect.width < 280);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="min-w-0 overflow-hidden rounded-lg bg-muted/50 p-3">
      <div className="mb-1 text-sm text-muted-foreground">{title}</div>
      {isStacked ? (
        <div className="space-y-1 text-sm">
          {buyerCbu && (
            <div>
              <span className="text-muted-foreground">CBU/Alias:</span>
              <br />
              <span
                className="cursor-pointer rounded text-xs break-all transition-colors hover:bg-muted/80"
                title="Clic para copiar"
                onClick={onCopyCbu}
              >
                {buyerCbu}
              </span>
            </div>
          )}
          {buyerBankName && (
            <div>
              <span className="text-muted-foreground">Banco:</span>
              <br />
              <span className="break-words text-foreground">{buyerBankName}</span>
            </div>
          )}
          {buyerAccountHolder && (
            <div>
              <span className="text-muted-foreground">Titular:</span>
              <br />
              <span className="break-words text-foreground">{buyerAccountHolder}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-0.5 text-sm">
          {buyerCbu && (
            <>
              <span className="text-muted-foreground">CBU:</span>
              <span className="min-w-0">
                <span
                  className="-mx-1 cursor-pointer rounded px-1 break-all transition-colors hover:bg-muted/80"
                  title="Clic para copiar"
                  onClick={onCopyCbu}
                >
                  {buyerCbu}
                </span>
              </span>
            </>
          )}
          {buyerBankName && (
            <>
              <span className="text-muted-foreground">Banco:</span>
              <span className="break-words text-foreground">{buyerBankName}</span>
            </>
          )}
          {buyerAccountHolder && (
            <>
              <span className="text-muted-foreground">Titular:</span>
              <span className="break-words text-foreground">{buyerAccountHolder}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentProofBlock({
  proofUrl,
  loading = false,
}: {
  proofUrl?: string | null;
  loading?: boolean;
}) {
  const [imageLoading, setImageLoading] = useState(true);

  if (!proofUrl && !loading) return null;

  return (
    <div className="space-y-2 rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-emerald-50/40 p-3 dark:border-emerald-800/50 dark:from-emerald-950/40 dark:to-emerald-950/20">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
          ✓ Comprobante de transferencia
        </span>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-32 w-full rounded-lg" />
          <div className="flex items-center justify-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
            <Loader2 className="h-3 w-3 animate-spin" />
            Cargando comprobante...
          </div>
        </div>
      ) : proofUrl ? (
        <div className="space-y-2">
          <div className="relative w-full overflow-hidden rounded-lg border border-emerald-200/50 bg-black/5 dark:border-emerald-800/30 dark:bg-white/5">
            <img
              src={proofUrl}
              alt="Comprobante de pago"
              className="h-auto w-full"
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
            />
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export const OrderSidebar: React.FC = () => {
  const { sidebarOpen, currentOrderId, closeSidebar, fromCheckout } = useOrderSidebar();
  const { setProductSidebarOpenedFromOrder, productSidebarOpenedFromOrder } = useOrderSidebar();
  const { openSidebar: openCheckoutSidebar } = useCheckout();
  const { setProductSidebarOpen, setSelectedProduct, productSidebarOpen } = useContentSidebar();
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();
  const { session, profile } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  useSidebarWidth();

  const sidebarRef = useRef<HTMLDivElement>(null);
  const addressPickerSidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const [isClosing, setIsClosing] = useState(false);
  const [order, setOrder] = useState<any | null>(null);
  const [store, setStore] = useState<any | null>(null);
  const [buyer, setBuyer] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [relatedNotifications, setRelatedNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [dispatchStatusModalOpen, setDispatchStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);
  const [editingDispatchStatus, setEditingDispatchStatus] = useState<string | null>(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [inlineCancelOpen, setInlineCancelOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    kind: 'status' | 'dispatch';
    value: string;
    label: string;
    remaining: number;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState<any | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [paymentSidebarOpen, setPaymentSidebarOpen] = useState(false);
  const [courierLiveLocation, setCourierLiveLocation] = useState<{
    lat: number;
    lng: number;
    updatedAt: string | null;
  } | null>(null);
  const [courierMapDebug, setCourierMapDebug] = useState<Record<string, unknown> | null>(null);
  const [addressPickerOpen, setAddressPickerOpen] = useState<'pickup' | 'delivery' | null>(null);
  const [addressPickerInitialLocation, setAddressPickerInitialLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [addressPickerInitialDisplayName, setAddressPickerInitialDisplayName] = useState<string | null>(null);
  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);

  // Order items come directly from RPC now (already hydrated with title and image_url)
  const orderItems = normalizeOrderItems(order?.order_items);

  const isOrderTrackable = !!order && !['delivered', 'cancelled'].includes(String(order?.dispatch_status || ''));
  const courierStage = readCourierStageFromNotes(activeAssignment?.notes);
  const hasCourierReachedLocal =
    courierStage === 'picked_up' ||
    courierStage === 'arrived_local' ||
    hasCourierReachedLocalByNotes(activeAssignment?.notes);

  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      closeSidebar();
      setIsClosing(false);

      if (fromCheckout) {
        setTimeout(() => {
          openCheckoutSidebar();
        }, 250);
      }
    }, 200);
  }, [closeSidebar, fromCheckout, openCheckoutSidebar]);

  const logCourierMapDebug = (payload: Record<string, unknown>) => {
    if (!COURIER_MAP_DEBUG) return;
    setCourierMapDebug(payload);
    console.log('[OrderSidebar][CourierMapDebug]', payload);
  };

  const openAddressPickerFromOrder = React.useCallback((addressType: 'pickup' | 'delivery') => {
    if (!order) return;

    const readNumber = (value: unknown): number | null => {
      const parsed = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    if (addressType === 'pickup') {
      const displayName = String(order?.delivery_pickup_address || order?.pickup_address_name || '').trim();
      const lat = readNumber(order?.delivery_pickup_latitude);
      const lng = readNumber(order?.delivery_pickup_longitude);

      setAddressPickerInitialDisplayName(displayName || null);
      setAddressPickerInitialLocation(lat !== null && lng !== null ? { lat, lng } : null);
      setAddressPickerOpen('pickup');
      return;
    }

    const displayName = String(order?.delivery_address || order?.delivery_address_name || '').trim();
    const lat = readNumber(order?.delivery_latitude);
    const lng = readNumber(order?.delivery_longitude);

    setAddressPickerInitialDisplayName(displayName || null);
    setAddressPickerInitialLocation(lat !== null && lng !== null ? { lat, lng } : null);
    setAddressPickerOpen('delivery');
  }, [order]);

  useEffect(() => {
    if (!sidebarOpen || !session?.user?.id) {
      setUserAddresses([]);
      return;
    }

    let active = true;

    const loadUserAddresses = async () => {
      try {
        const addresses = await getUserAddresses(session.user.id);
        if (active) setUserAddresses(addresses || []);
      } catch (err) {
        console.warn('[OrderSidebar] user addresses fetch failed', err);
        if (active) setUserAddresses([]);
      }
    };

    void loadUserAddresses();

    return () => {
      active = false;
    };
  }, [sidebarOpen, session?.user?.id]);

  // Listen for event to reopen order sidebar when product sidebar closes
  useEffect(() => {
    const handleReopenOrderSidebar = () => {
      if (currentOrderId) {
        // Just ensure sidebar is open
        if (!sidebarOpen) {
          // Do nothing - the sidebar should already be in the right state
          // The event is just a signal that ProductSidebar closed
        }
      }
    };

    window.addEventListener('reopen-order-sidebar', handleReopenOrderSidebar);
    return () => window.removeEventListener('reopen-order-sidebar', handleReopenOrderSidebar);
  }, [currentOrderId, sidebarOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusModalOpen || dispatchStatusModalOpen || confirmCancelOpen || paymentSidebarOpen || !!addressPickerOpen) {
        return;
      }

      if (sidebarOpen && !isMinimized && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        const el = event.target as HTMLElement | null;
        if (!el || !el.isConnected) return;

        const isPortal =
          el.closest('[data-radix-portal]') ||
          el.closest('[data-radix-popper-content-wrapper]') ||
          el.closest('[role="dialog"]') ||
          el.closest('[role="listbox"]') ||
          el.closest('.leaflet-container') ||
          el.closest('[toast-close]') ||
          el.closest('[data-radix-toast-announcer]');

        if (isPortal) return;
        handleClose();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    sidebarOpen,
    isMinimized,
    handleClose,
    statusModalOpen,
    dispatchStatusModalOpen,
    confirmCancelOpen,
    paymentSidebarOpen,
    addressPickerOpen,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && sidebarOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [sidebarOpen, handleClose]);

  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop,
  });

  useScrollLock((sidebarOpen && !isMinimized) || isClosing, false, scrollAreaRef);

  useEffect(() => {
    if (sidebarOpen) {
      recalculate();
      const timeoutId1 = setTimeout(recalculate, 0);
      const timeoutId2 = setTimeout(recalculate, 50);
      const timeoutId3 = setTimeout(recalculate, 100);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [sidebarOpen, recalculate]);

  useEffect(() => {
    let mounted = true;

    const loadOrder = async () => {
      console.log('🔵 [OrderSidebar] loadOrder START for orderId:', currentOrderId);
      
      if (!currentOrderId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setOrder(null);
      setStore(null);
      setBuyer(null);
      setRelatedNotifications([]);
      setActiveAssignment(null);
      setAssignments([]);

      try {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('get_order_if_authorized', {
          p_order_id: currentOrderId,
        });

        let orderData;

        if (rpcErr) {
          console.error('[OrderSidebar] RPC error:', rpcErr);
          throw rpcErr;
        }

        orderData = Array.isArray(rpcData) ? rpcData[0] : rpcData;

        if (!orderData) {
          setError('Acceso denegado o pedido no encontrado');
          setLoading(false);
          return;
        }

        if (!mounted) return;

        // Fetch order items - read directly from order_items table
        let baseOrderItems: any[] = [];
        try {
          console.log('[OrderSidebar] 🔍 Attempting to fetch items for order:', currentOrderId);
          
          // Query order_items table directly (title and image are stored in the table)
          console.log('[OrderSidebar] 📝 Fetching from order_items...');
          const { data: directItems, error: directErr } = await supabase
            .from('order_items')
            .select('id, order_id, product_id, quantity, unit_price, title, image_url, details, created_at, updated_at')
            .eq('order_id', currentOrderId);

          if (directErr) {
            console.error('[OrderSidebar] ❌ Query failed:', directErr.code, directErr.message);
          } else {
            console.log('[OrderSidebar] ✅ Query succeeded!');
            console.log('[OrderSidebar] Got', directItems?.length || 0, 'items');
            if (directItems && directItems.length > 0) {
              console.log('[OrderSidebar] First item:', JSON.stringify(directItems[0], null, 2));
            }
            baseOrderItems = directItems || [];
          }
        } catch (e) {
          console.error('[OrderSidebar] ❌ Exception:', (e as any).message);
        }

        console.log('[OrderSidebar] 📦 Final items count before normalize:', baseOrderItems.length);
        
        // Normalize the items
        const normalizedFetchedItems = normalizeOrderItems(baseOrderItems);
        console.log('[OrderSidebar] ✅ After normalize:', normalizedFetchedItems.length);
        for (let i = 0; i < normalizedFetchedItems.length; i++) {
          console.log(`  [${i}] ${normalizedFetchedItems[i].title}`);
        }

        setOrder({
          ...orderData,
          order_items: normalizedFetchedItems,
        });

        try {
          if (orderData.user_id) {
            const { data: buyerProfile } = await supabase
              .from('profiles')
              .select('id, username, avatar_url')
              .eq('id', orderData.user_id)
              .maybeSingle();

            if (mounted && buyerProfile) setBuyer(buyerProfile);
          }
        } catch (e) {
          console.warn('buyer profile fetch failed', e);
        }

        try {
          const storeId = orderData?.store_id;
          if (storeId) {
            const member = await isUserMember(storeId);
            if (mounted) setIsMember(!!member);

            try {
              const storeData = await getStoreById(storeId);
              if (mounted) setStore(storeData || null);
            } catch (e) {
              console.warn('getStoreById failed', e);
            }
          }
        } catch (e) {
          console.warn('isUserMember check failed', e);
        }

        try {
          const active = await getActiveAssignmentForOrder(currentOrderId);
          if (mounted) setActiveAssignment(active);

          const allAssignments = await getAssignmentsByOrderId(currentOrderId);
          if (mounted) setAssignments(allAssignments);
        } catch (e) {
          console.warn('assignment fetch failed', e);
        }

        setAuthorized(true);
        setLoading(false);

        try {
          setNotificationsLoading(true);

          const { data: notes, error: notesErr } = await supabase
            .from('notifications')
            .select('*')
            .or(`link.ilike.%${currentOrderId}%,message.ilike.%${currentOrderId}%`)
            .order('created_at', { ascending: false });

          if (notesErr) console.warn('notifications fetch error', notesErr);
          if (mounted) setRelatedNotifications(notes || []);
        } catch (e) {
          console.warn('notifications fetch thrown', e);
        } finally {
          if (mounted) setNotificationsLoading(false);
        }
      } catch (e: any) {
        console.error(e);
        setError(String(e));
        setLoading(false);
      }
    };

    void loadOrder();

    return () => {
      mounted = false;
    };
  }, [currentOrderId, session, profile, reloadKey]);

  useEffect(() => {
    if (!currentOrderId) return;

    const channel = supabase
      .channel(`order-sidebar-assignment:${currentOrderId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'order_dispatch_assignments',
          filter: `order_id=eq.${currentOrderId}`,
        },
        async () => {
          try {
            const active = await getActiveAssignmentForOrder(currentOrderId);
            setActiveAssignment(active);
          } catch (e) {
            console.warn('assignment realtime sync failed', e);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrderId]);

  useEffect(() => {
    if (!activeAssignment?.courier_id || !isOrderTrackable || !hasCourierReachedLocal) {
      setCourierLiveLocation(null);
      logCourierMapDebug({
        reason: 'blocked_by_gate',
        hasCourierId: Boolean(activeAssignment?.courier_id),
        isOrderTrackable,
        courierStage,
        hasCourierReachedLocal,
        orderDispatchStatus: order?.dispatch_status || null,
      });
      return;
    }

    let cancelled = false;
    const courierId = activeAssignment.courier_id as string;

    const loadCourierLocation = async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('relevant_latitude, relevant_longitude, last_location, last_location_updated_at')
        .eq('id', courierId)
        .maybeSingle();

      if (cancelled || error || !data) {
        logCourierMapDebug({
          reason: 'profile_fetch_failed_or_empty',
          cancelled,
          hasData: Boolean(data),
          error: error?.message || null,
          courierId,
        });
        return;
      }

      const coords = readCourierCoordsFromProfileRow(data);
      const updatedAt = data.last_location_updated_at || null;
      const isFresh = updatedAt
        ? Date.now() - new Date(updatedAt).getTime() <= COURIER_LOCATION_MAX_AGE_MS
        : false;

      logCourierMapDebug({
        reason: 'profile_loaded',
        courierId,
        stage: courierStage,
        isOrderTrackable,
        hasCourierReachedLocal,
        hasCoords: Boolean(coords),
        coords,
        updatedAt,
        isFresh,
        rawLastLocation: data.last_location || null,
        rawRelevantLat: data.relevant_latitude ?? null,
        rawRelevantLng: data.relevant_longitude ?? null,
      });

      if (coords && isFresh) {
        setCourierLiveLocation({
          lat: coords.lat,
          lng: coords.lng,
          updatedAt,
        });
      } else {
        setCourierLiveLocation(null);
      }
    };

    void loadCourierLocation();

    const channel = supabase
      .channel(`order-sidebar-courier-live:${courierId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${courierId}`,
        },
        (payload: any) => {
          const next = payload.new || {};
          const coords = readCourierCoordsFromProfileRow(next);
          const updatedAt = next.last_location_updated_at || null;
          const isFresh = updatedAt
            ? Date.now() - new Date(updatedAt).getTime() <= COURIER_LOCATION_MAX_AGE_MS
            : false;

          logCourierMapDebug({
            reason: 'profile_realtime_update',
            courierId,
            stage: courierStage,
            isOrderTrackable,
            hasCourierReachedLocal,
            hasCoords: Boolean(coords),
            coords,
            updatedAt,
            isFresh,
            rawLastLocation: next.last_location || null,
            rawRelevantLat: next.relevant_latitude ?? null,
            rawRelevantLng: next.relevant_longitude ?? null,
          });

          if (coords && isFresh) {
            setCourierLiveLocation({
              lat: coords.lat,
              lng: coords.lng,
              updatedAt,
            });
          } else {
            setCourierLiveLocation(null);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [activeAssignment?.courier_id, isOrderTrackable, hasCourierReachedLocal, courierStage, order?.dispatch_status]);

  useEffect(() => {
    if (!courierLiveLocation?.updatedAt) return;

    const expiresIn =
      COURIER_LOCATION_MAX_AGE_MS - (Date.now() - new Date(courierLiveLocation.updatedAt).getTime());

    if (expiresIn <= 0) {
      setCourierLiveLocation(null);
      return;
    }

    const timeout = setTimeout(() => {
      setCourierLiveLocation(null);
    }, expiresIn);

    return () => clearTimeout(timeout);
  }, [courierLiveLocation?.updatedAt]);

  const computeDeliveryTotal = (currentOrder: any) => {
    if (!currentOrder) return 0;

    const base = Number(currentOrder.delivery_fee ?? 0);
    const systemFee = Number(currentOrder.system_fee ?? 0);
    const hunterFee = Number(currentOrder.hunter_fee ?? 0);
    let proportional = 0;

    const items = normalizeOrderItems(currentOrder.order_items);
    for (const item of items) {
      proportional += Number(
        item.proportional_shipping ?? item.shipping_share ?? item.delivery_share ?? item.delivery_fee ?? 0
      );
    }

    const total = base + systemFee + hunterFee + proportional;
    return Number(total.toFixed(2));
  };

  const handleReleaseChanga = async () => {
    if (!activeAssignment) return;

    try {
      const { data: releaseResult, error: rpcError } = await supabase.rpc('release_changa', {
        p_order_id: order.id,
        p_courier_id: activeAssignment.courier_id,
      });

      const rpcSuccess = Boolean(releaseResult && (releaseResult as any).success === true);

      if (rpcError || !rpcSuccess) {
        await releaseOrderAssignment(activeAssignment.id);
        await supabase.from('orders').update({ dispatch_status: 'published' }).eq('id', order.id);
        await supabase.from('profiles').update({ active_changa_id: null }).eq('id', activeAssignment.courier_id);
      }

      setReloadKey((current) => current + 1);
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudo liberar la changa.', variant: 'destructive' });
    }
  };

  const handleCompleteChanga = async () => {
    if (!activeAssignment) return;

    try {
      await completeOrderAssignment(activeAssignment.id);
      await supabase.from('orders').update({ dispatch_status: 'delivered' }).eq('id', order.id);
      setReloadKey((current) => current + 1);
    } catch (e) {
      console.error(e);
    }
  };

  const confirmCancel = async () => {
    try {
      await updateOrderStatus(order.id, 'cancelled');
      setStatusModalOpen(false);
      setEditingStatus(null);
      setReloadKey((current) => current + 1);
    } catch (e) {
      console.error('Failed to cancel order', e);
    }

    setConfirmCancelOpen(false);
  };

  const cancelPendingStatusChange = () => {
    setPendingStatusChange(null);
  };

  const submitDispatchStatusChange = async (value: string) => {
    setEditingDispatchStatus(value);

    const { error } = await supabase
      .from('orders')
      .update({ dispatch_status: value, updated_at: new Date().toISOString() })
      .eq('id', order.id);

    if (error) {
      console.error('[ERROR] Supabase update dispatch_status', error);
      alert('Error actualizando dispatch_status: ' + (error.message || error.details || error.code));
      return;
    }

    if (value === 'published') {
      const { error: changaError } = await insertChangaBoard(order.id, session);
      if (changaError) {
        console.error('[ERROR] Insert changas_board', changaError);
        alert('Error agregando al board: ' + (changaError.message || changaError.details || changaError.code));
        return;
      }
    }

    setOrder({ ...order, dispatch_status: value });
    setDispatchStatusModalOpen(false);
    setTimeout(() => setEditingDispatchStatus(null), 300);
  };

  const startStatusChangeCountdown = (
    kind: 'status' | 'dispatch',
    value: string,
    label: string
  ) => {
    setPendingStatusChange({ kind, value, label, remaining: 4 });
  };

  useEffect(() => {
    if (!pendingStatusChange) return;

    const intervalId = window.setInterval(() => {
      setPendingStatusChange((previous) => {
        if (!previous) return null;
        if (previous.remaining <= 1) return null;
        return { ...previous, remaining: previous.remaining - 1 };
      });
    }, 1000);

    const timeoutId = window.setTimeout(async () => {
      const pending = pendingStatusChange;
      setPendingStatusChange(null);

      try {
        if (pending.kind === 'status') {
          if (pending.value === 'cancelled') {
            setEditingStatus(pending.value);
            setConfirmCancelOpen(true);
            return;
          }

          setEditingStatus(pending.value);
          await updateOrderStatus(order.id, pending.value);
          setOrder({ ...order, status: pending.value });
          setStatusModalOpen(false);
          setTimeout(() => setEditingStatus(null), 300);
          setReloadKey((current) => current + 1);
          return;
        }

        await submitDispatchStatusChange(pending.value);
      } catch (e) {
        console.error('Error aplicando cambio de estado con countdown', e);
      }
    }, pendingStatusChange.remaining * 1000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [pendingStatusChange, order, session]);

  return (
    <>
      {(sidebarOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 bg-transparent z-[9997] transition-opacity duration-200',
            isClosing && 'pointer-events-none opacity-0'
          )}
          onClick={handleClose}
        />
      )}

      <div
        ref={sidebarRef}
        className={cn(
          'fixed right-0 z-[9998] flex w-full flex-col overflow-x-hidden bg-background shadow-2xl lg:w-[400px]',
          'transition-transform duration-200',
          sidebarOpen && !isClosing ? 'translate-x-0' : 'translate-x-full',
          !sidebarOpen && !isClosing && !isMinimized && 'invisible'
        )}
        style={{
          top: isDesktop ? `${headerHeight}px` : '0',
          bottom: isDesktop ? undefined : '0',
          height: isDesktop ? `calc(100vh - ${headerHeight}px)` : `calc(100dvh + ${bottomNavHeight}px)`,
          pointerEvents: sidebarOpen && !isClosing && !isMinimized ? 'auto' : 'none',
        }}
      >
        {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] w-full shrink-0 bg-primary" />}

        <div className="flex min-h-[64px] min-w-0 items-center justify-between gap-2 overflow-hidden border-b bg-primary p-4 text-primary-foreground shrink-0">
          <h2 className="flex min-w-0 flex-1 items-center gap-2 text-lg font-semibold text-primary-foreground">
            <ReceiptText className="h-5 w-5 text-primary-foreground" />
            <span className="truncate">{order ? `Pedido #${getOrderDisplayNumber(order)}` : 'Pedido'}</span>
          </h2>

          <div className="flex shrink-0 items-center gap-2">
            <TooltipProvider>
              {!isDesktop && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      onClick={() => setIsMinimized(true)}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Minimizar</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {currentOrderId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      onClick={() => {
                        handleClose();
                        navigate(`/orden/${currentOrderId}`);
                      }}
                    >
                      <Maximize2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Ampliar</p>
                  </TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClose();
                    }}
                    className="rounded-lg p-1 text-white transition-colors hover:bg-white/10 hover:text-white"
                  >
                    <X className="h-5 w-5 text-white" />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="sidebar">
                  <p>Cerrar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <ScrollArea className="flex-1 overflow-x-hidden" ref={scrollAreaRef}>
          <div className="min-w-0 overflow-x-hidden p-4">
            {loading ? (
              <div className="space-y-4">
                <div className="mb-3 flex items-center space-x-3">
                  <Skeleton className="h-10 w-10 rounded-md" />
                  <div>
                    <Skeleton className="mb-1 h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Skeleton className="h-20" />
                  <Skeleton className="h-20" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                </div>
              </div>
            ) : error ? (
              <div className="p-4 text-center text-destructive">{error}</div>
            ) : authorized === false ? (
              <div className="p-4 text-center text-muted-foreground">Acceso denegado.</div>
            ) : !order ? (
              <div className="p-4 text-center text-muted-foreground">Pedido no encontrado.</div>
            ) : (
              <>
                {(() => {
                  const orderTotalAmount = Number(order?.total || 0);
                  const deliveryTotalAmount = Number(computeDeliveryTotal(order));
                  const paidToConfirmAmount = orderTotalAmount;
                  const remainingOnDeliveryAmount = deliveryTotalAmount;
                  const storeStatusText = getStoreStatusLabel(order?.status);
                  const dispatchStatusText = getDispatchStatusLabel(order?.dispatch_status);

                  return (
                    <>
                      <div className="mb-4 rounded-lg border bg-muted/20 p-3">
                        <div className="mb-2 text-xs font-medium text-muted-foreground">Avance del pedido</div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Tienda ({storeStatusText})
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {getStoreStatusProgress(order?.status)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-primary transition-all duration-300"
                                style={{
                                  width: `${Math.min(100, Math.max(0, getStoreStatusProgress(order?.status)))}%`,
                                }}
                              />
                            </div>
                          </div>

                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-medium text-muted-foreground">
                                Envío ({dispatchStatusText})
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {getDispatchStatusProgress(order?.dispatch_status)}%
                              </span>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                                style={{
                                  width: `${Math.min(100, Math.max(0, getDispatchStatusProgress(order?.dispatch_status)))}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-lg border p-3">
                          <button
                            onClick={() => isMember && setStatusModalOpen(!statusModalOpen)}
                            disabled={!isMember}
                            className={cn(
                              'w-full text-left transition-opacity',
                              isMember && 'cursor-pointer hover:opacity-80'
                            )}
                          >
                            <div className="mb-2 text-xs text-muted-foreground">Estado en tienda</div>
                            <div
                              className={cn(
                                'text-sm font-medium',
                                isMember ? 'cursor-pointer text-primary underline' : 'text-foreground'
                              )}
                            >
                              {getStoreStatusLabel(order.status)}
                            </div>
                          </button>

                          {statusModalOpen && isMember && (
                            <div className="mt-3 space-y-2 border-t pt-3">
                              {pendingStatusChange?.kind === 'status' && (
                                <div className="rounded-lg border border-primary/25 bg-primary/10 p-2.5">
                                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                                    <div className="min-w-0 break-words text-xs font-medium text-primary">
                                      Cambiando estado a {pendingStatusChange.label} en {pendingStatusChange.remaining}s
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={cancelPendingStatusChange}
                                      className="h-7 px-2 text-xs"
                                    >
                                      <X className="mr-1 h-3 w-3" />
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {confirmCancelOpen ? (
                                <div className="space-y-3 p-2">
                                  <div className="text-sm font-medium">¿Confirmar cancelación?</div>
                                  <div className="text-xs text-muted-foreground">
                                    El comprador será notificado que su pedido ha sido cancelado.
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setConfirmCancelOpen(false)}
                                      className="flex-1"
                                    >
                                      No cancelar
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      className="flex-1 hover:bg-destructive/90"
                                      onClick={() => confirmCancel()}
                                    >
                                      Sí, cancelar
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {STORE_STATUS_SETTING_OPTIONS.map((statusValue, index) => (
                                    <button
                                      key={statusValue}
                                      onClick={() => {
                                        startStatusChangeCountdown(
                                          'status',
                                          statusValue,
                                          getStoreStatusLabel(statusValue)
                                        );
                                      }}
                                      className={cn(
                                        'w-full cursor-pointer rounded border p-2 text-left text-xs transition-all',
                                        'animate-in slide-in-from-bottom duration-200',
                                        (editingStatus ?? order?.status) === statusValue
                                          ? 'border-primary bg-primary/10 font-medium'
                                          : 'border-muted bg-muted/30 hover:bg-muted/50'
                                      )}
                                      style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                      {getStoreStatusLabel(statusValue)}
                                    </button>
                                  ))}
                                </>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="rounded-lg border p-3">
                          <button
                            onClick={() => isMember && setDispatchStatusModalOpen(!dispatchStatusModalOpen)}
                            disabled={!isMember}
                            className={cn(
                              'w-full text-left transition-opacity',
                              isMember && 'cursor-pointer hover:opacity-80'
                            )}
                          >
                            <div className="mb-2 text-xs text-muted-foreground">Estado de envío</div>
                            <div
                              className={cn(
                                'text-sm font-medium',
                                isMember ? 'cursor-pointer text-primary underline' : 'text-foreground'
                              )}
                            >
                              {getDispatchStatusLabel(order.dispatch_status)}
                            </div>
                          </button>

                          {dispatchStatusModalOpen && isMember && (
                            <div className="mt-3 space-y-2 border-t pt-3">
                              {pendingStatusChange?.kind === 'dispatch' && (
                                <div className="rounded-lg border border-primary/25 bg-primary/10 p-2.5">
                                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                                    <div className="min-w-0 break-words text-xs font-medium text-primary">
                                      Cambiando estado a {pendingStatusChange.label} en {pendingStatusChange.remaining}s
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={cancelPendingStatusChange}
                                      className="h-7 px-2 text-xs"
                                    >
                                      <X className="mr-1 h-3 w-3" />
                                      Cancelar
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {DISPATCH_STATUS_SETTING_OPTIONS.map((dispatchValue, index) => (
                                <button
                                  key={dispatchValue}
                                  onClick={() => {
                                    startStatusChangeCountdown(
                                      'dispatch',
                                      dispatchValue,
                                      getDispatchStatusLabel(dispatchValue)
                                    );
                                  }}
                                  className={cn(
                                    'w-full cursor-pointer rounded border p-2 text-left text-xs transition-all',
                                    'animate-in slide-in-from-bottom duration-200',
                                    (editingDispatchStatus ?? order?.dispatch_status) === dispatchValue
                                      ? 'border-primary bg-primary/10 font-medium'
                                      : 'border-muted bg-muted/30 hover:bg-muted/50'
                                  )}
                                  style={{ animationDelay: `${index * 30}ms` }}
                                >
                                  {getDispatchStatusLabel(dispatchValue)}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {store && (
                        <div className="mb-4 flex min-w-0 items-center space-x-3 overflow-hidden rounded-lg bg-muted/30 p-3">
                          <Avatar className="rounded-md">
                            {store.avatar_url ? (
                              <AvatarImage className="rounded-md" src={store.avatar_url} />
                            ) : (
                              <AvatarFallback className="rounded-md">{(store.name || 'S')[0]}</AvatarFallback>
                            )}
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="truncate font-medium">{store.name}</div>
                            <Link
                              to={`/${store.slug}`}
                              className="block truncate text-sm text-muted-foreground hover:underline"
                            >
                              minibid.org/{store.slug}
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Delivery Tracking Panel */}
                      <DeliveryTrackingPanel
                        order={order}
                        activeAssignment={activeAssignment}
                        courierLiveLocation={courierLiveLocation}
                        isOrderTrackable={isOrderTrackable}
                        loading={loading}
                      />

                      {/* Courier actions (if user is the courier) */}
                      {activeAssignment && session?.user?.id === activeAssignment.courier_id && (
                        <div className="mb-4 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleReleaseChanga}
                            className="flex-1"
                          >
                            Liberar changa
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={handleCompleteChanga}
                            className="flex-1"
                          >
                            Entregar
                          </Button>
                        </div>
                      )}

                      {/* Debug panel */}
                      {COURIER_MAP_DEBUG && activeAssignment && (
                        <div className="mb-4 space-y-1 rounded-md border border-dashed border-amber-400/70 bg-amber-50/50 p-2 text-[11px] text-amber-900">
                          <div className="font-semibold">Debug courier tracking</div>
                          <div>stage: {String(courierStage || 'null')}</div>
                          <div>assignment_status: {String(activeAssignment?.status || 'null')}</div>
                          <div>trackable: {String(isOrderTrackable)}</div>
                          <div>reached_local: {String(hasCourierReachedLocal)}</div>
                          <div>has_live_location: {String(Boolean(courierLiveLocation))}</div>
                          <div className="break-all">notes: {String(activeAssignment?.notes || 'null')}</div>
                          {courierMapDebug && (
                            <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded border border-amber-300/60 bg-white/70 p-1 text-[10px]">
                              {JSON.stringify(courierMapDebug, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}

                      {order.is_auction_order && order.status === 'pending_payment' && order.payment_deadline && (
                        <div className="mb-4 space-y-3 rounded-lg border-2 border-warning bg-warning-muted p-4">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <AlertCircle className="h-5 w-5 shrink-0 text-warning" />
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                                  ¡Ganaste la subasta!
                                </div>
                                <div className="mt-0.5 break-words text-xs text-amber-700 dark:text-amber-300">
                                  Completa el pago antes de que expire el tiempo
                                </div>
                              </div>
                            </div>
                            <Badge variant="outline" className="shrink-0 border-warning bg-warning-muted text-warning">
                              Pago pendiente
                            </Badge>
                          </div>

                          <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-md border border-warning bg-card p-3">
                            <span className="min-w-0 text-xs font-medium text-amber-900 dark:text-amber-100">
                              Tiempo restante:
                            </span>
                            <AuctionOrderCountdown
                              deadline={order.payment_deadline}
                              onExpire={() => {
                                toast({
                                  title: '⏰ Tiempo agotado',
                                  description: 'El plazo para pagar ha expirado',
                                  variant: 'destructive',
                                });
                                setReloadKey((current) => current + 1);
                              }}
                            />
                          </div>

                          {session?.user?.id === order.user_id && (
                            <Button
                              className="w-full bg-amber-600 text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-800"
                              onClick={() => setPaymentSidebarOpen(true)}
                            >
                              <CreditCard className="mr-2 h-4 w-4" />
                              Completar pago ahora
                            </Button>
                          )}
                        </div>
                      )}

                      {buyer && (
                        <div className="mb-4 space-y-3">
                          <div className="flex min-w-0 items-start gap-3 overflow-hidden rounded-lg bg-muted/50 p-3">
                            <Avatar className="h-10 w-10">
                              {buyer.avatar_url ? (
                                <AvatarImage src={buyer.avatar_url} />
                              ) : (
                                <AvatarFallback>{getAvatarInitials(buyer.username || 'Usuario')}</AvatarFallback>
                              )}
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-muted-foreground">Comprador</div>
                              <Link
                                to={`/${buyer.username}`}
                                className="block truncate font-medium hover:underline"
                              >
                                {buyer.username || 'Usuario'}
                              </Link>
                            </div>
                          </div>

                          {(order?.buyer_cbu || order?.buyer_bank_name) && (
                            <BankDataBlock
                              buyerCbu={order?.buyer_cbu}
                              buyerBankName={order?.buyer_bank_name}
                              buyerAccountHolder={order?.buyer_account_holder}
                              onCopyCbu={async () => {
                                try {
                                  await navigator.clipboard.writeText(order.buyer_cbu);
                                  toast({ title: 'CBU copiado' });
                                } catch {}
                              }}
                            />
                          )}

                          {order?.payment_proof_url && (
                            <PaymentProofBlock proofUrl={order.payment_proof_url} loading={false} />
                          )}
                        </div>
                      )}

                      {!buyer && (order?.buyer_cbu || order?.buyer_bank_name || order?.payment_proof_url) && (
                        <div className="mb-4">
                          {(order?.buyer_cbu || order?.buyer_bank_name) && (
                            <BankDataBlock
                              buyerCbu={order?.buyer_cbu}
                              buyerBankName={order?.buyer_bank_name}
                              buyerAccountHolder={order?.buyer_account_holder}
                              onCopyCbu={async () => {
                                try {
                                  await navigator.clipboard.writeText(order.buyer_cbu);
                                  toast({ title: 'CBU copiado' });
                                } catch {}
                              }}
                              title="Datos de pago declarados"
                            />
                          )}

                          {order?.payment_proof_url && (
                            <div className={order?.buyer_cbu || order?.buyer_bank_name ? 'mt-3' : ''}>
                              <PaymentProofBlock proofUrl={order.payment_proof_url} loading={false} />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mb-6 space-y-4">
                        <div className="space-y-1 rounded-xl border-2 border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-4">
                          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Total pagado a confirmar
                          </p>
                          <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                            <span className="text-2xl font-bold text-primary sm:text-3xl">
                              {formatCurrency(paidToConfirmAmount)}
                            </span>
                          </div>
                          <div className="mt-3 flex min-w-0 items-center justify-between gap-2 border-t border-primary/20 pt-2">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Restante a pagar al recibir al repartidor
                            </span>
                            <span className="shrink-0 text-sm font-bold text-foreground">
                              {formatCurrency(remainingOnDeliveryAmount)}
                            </span>
                          </div>
                        </div>

                        {(orderTotalAmount > 0 || deliveryTotalAmount > 0) && (
                          <div className="rounded-lg border bg-muted/30 p-3">
                            <div className="mb-2 text-xs font-medium text-muted-foreground">Desglose</div>
                            <div className="space-y-1.5 text-sm">
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                <span className="min-w-0 truncate text-muted-foreground">Productos</span>
                                <span className="shrink-0 text-right font-medium tabular-nums">
                                  {formatCurrency(orderTotalAmount)}
                                </span>
                              </div>
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                <span className="min-w-0 truncate text-muted-foreground">+ Envío</span>
                                <span className="shrink-0 text-right font-medium tabular-nums">
                                  {formatCurrency(deliveryTotalAmount)}
                                </span>
                              </div>
                              <div className="mt-1.5 flex min-w-0 items-center justify-between gap-2 border-t border-border pt-1.5">
                                <span className="min-w-0 truncate font-medium text-foreground">Total</span>
                                <span className="shrink-0 text-right font-bold tabular-nums">
                                  {formatCurrency(orderTotalAmount + deliveryTotalAmount)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="rounded-lg border p-3">
                          <div className="mb-1 text-xs text-muted-foreground">Creado</div>
                          <div className="text-sm font-medium">
                            {order.created_at ? format(new Date(order.created_at), 'dd/MM/yyyy HH:mm') : '—'}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                <div className="mb-4">
                  <h4 className="mb-2 font-medium">Items del pedido</h4>
                  <OrderItemsList 
                    items={orderItems}
                    onItemClick={async (productId) => {
                      if (loadingProductId === productId) return;
                      try {
                        setLoadingProductId(productId);
                        const fullProduct = await getProductById(productId);
                        if (fullProduct) {
                          setProductSidebarOpenedFromOrder(true);
                          setSelectedProduct(fullProduct);
                          setProductSidebarOpen(true);
                        }
                      } catch (e) {
                        console.error('Error loading product:', e);
                        toast({
                          title: 'Error',
                          description: 'No se pudo cargar el producto',
                          variant: 'destructive',
                        });
                      } finally {
                        setLoadingProductId(null);
                      }
                    }}
                  />
                </div>

                {(order?.delivery_pickup_address ||
                  order?.pickup_address_name ||
                  order?.delivery_address ||
                  order?.delivery_address_name) && (
                  <div className="mb-4 grid grid-cols-1 gap-3">
                    {(order?.delivery_pickup_address || order?.pickup_address_name) && (
                      <div className="rounded-lg border p-3">
                        <div className="mb-1 text-xs text-muted-foreground">Punto de retiro</div>
                        <button
                          onClick={() => openAddressPickerFromOrder('pickup')}
                          className="block w-full truncate text-left text-sm font-medium text-primary hover:underline"
                        >
                          {order?.delivery_pickup_address || order?.pickup_address_name}
                        </button>
                      </div>
                    )}

                    {(order?.delivery_address || order?.delivery_address_name) && (
                      <div className="rounded-lg border p-3">
                        <div className="mb-1 text-xs text-muted-foreground">Punto de destino</div>
                        <button
                          onClick={() => openAddressPickerFromOrder('delivery')}
                          className="block w-full truncate text-left text-sm font-medium text-primary hover:underline"
                        >
                          {order?.delivery_address || order?.delivery_address_name}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {order?.notes && (
                  <div className="mb-4 rounded border-l-4 border-primary bg-muted/50 p-3">
                    <div className="mb-1 text-xs text-muted-foreground">Notas del pedido</div>
                    {renderOrderNotes(order.notes)}
                  </div>
                )}

                <div className="mb-4">
                  <h4 className="mb-2 font-medium">Actividad</h4>
                  {notificationsLoading ? (
                    <div className="text-sm text-muted-foreground">Cargando...</div>
                  ) : relatedNotifications.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      No hay actividad relacionada con este pedido.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {relatedNotifications.map((notification: any) => (
                        <li key={notification.id} className="min-w-0 overflow-hidden rounded border p-2">
                          <div className="break-words text-sm">
                            {formatMessageWithCurrency(notification.message)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {notification.created_at
                              ? format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm:ss')
                              : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {(() => {
                  const isBuyer = session?.user?.id === order?.user_id;
                  const dispatchStatusKey = order?.dispatch_status || 'pending';
                  const canCancelBeforeCourier = !['picked_up', 'delivered', 'completed', 'cancelled'].includes(
                    dispatchStatusKey
                  );
                  const deliveryTotalAmount = Number(computeDeliveryTotal(order));

                  if (
                    !isBuyer ||
                    order?.dispatch_status === 'delivered' ||
                    order?.dispatch_status === 'completed' ||
                    order?.status === 'cancelled' ||
                    order?.dispatch_status === 'cancelled'
                  ) {
                    return null;
                  }

                  return (
                    <div className="mt-6 flex justify-center text-center">
                      <div className="w-full max-w-sm">
                        {canCancelBeforeCourier ? (
                          <AnimatePresence mode="wait" initial={false}>
                            {inlineCancelOpen ? (
                              <motion.div
                                key="confirm-cancel"
                                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                                transition={{ duration: 0.16, ease: 'easeOut' }}
                                className="space-y-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3 text-left"
                              >
                                <p className="font-sans text-xs font-medium leading-tight text-destructive">
                                  Seguro que quieres cancelar este pedido. La tienda gestionará tu devolución.
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setInlineCancelOpen(false);
                                    }}
                                    className="w-full flex-1 rounded-full border border-border bg-background px-3 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted"
                                  >
                                    No Cancelar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      void confirmCancel();
                                      setInlineCancelOpen(false);
                                    }}
                                    className="w-full flex-1 rounded-full border border-destructive/25 bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive transition-colors hover:bg-destructive/15"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </motion.div>
                            ) : (
                              <motion.button
                                key="cancel-button"
                                type="button"
                                initial={{ opacity: 0, y: 4, scale: 0.98 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 4, scale: 0.98 }}
                                transition={{ duration: 0.16, ease: 'easeOut' }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setInlineCancelOpen(true);
                                }}
                                className="mx-auto inline-flex w-fit items-center justify-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-xs font-semibold text-muted-foreground/80 transition-all hover:text-foreground"
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancelar pedido
                              </motion.button>
                            )}
                          </AnimatePresence>
                        ) : (
                          <div className="space-y-2 text-left">
                            <button
                              type="button"
                              disabled
                              aria-disabled="true"
                              className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full border border-border bg-muted/60 px-3 py-2 text-xs font-semibold text-muted-foreground/80 opacity-75"
                            >
                              <X className="h-3.5 w-3.5" />
                              Cancelar pedido
                            </button>
                            <p className="font-sans text-[11px] leading-snug text-muted-foreground">
                              Tu repartidor ya está yendo a entregarte el pedido. Recíbelo y gestiona la devolución con
                              la tienda. Debes pagar{' '}
                              {deliveryTotalAmount > 0
                                ? formatCurrency(deliveryTotalAmount)
                                : 'el costo del envío'}{' '}
                              al repartidor por su servicio canceles o no.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-4 break-all text-center text-xs text-muted-foreground">ID: {order.id}</div>

                <div className="h-[calc(4rem+env(safe-area-inset-bottom,0px))] md:hidden" />
              </>
            )}
          </div>
        </ScrollArea>
      </div>

      {!isDesktop && isMinimized && sidebarOpen && (
        <div
          className="fixed bottom-0 left-0 right-0 z-[100] w-full rounded-t-lg border bg-background shadow-lg transition-all duration-200 md:left-auto md:w-80"
          style={{ bottom: `${bottomNavHeight}px` }}
        >
          <button
            onClick={() => setIsMinimized(false)}
            className="flex w-full items-center justify-between p-3 transition-colors hover:bg-primary/10"
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <ReceiptText className="h-5 w-5 shrink-0" />
              <span className="truncate font-medium">Pedido #{getOrderDisplayNumber(order)}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </button>
        </div>
      )}

      {order && order.is_auction_order && order.status === 'pending_payment' && (
        <PaymentSidebar
          isOpen={paymentSidebarOpen}
          onClose={() => setPaymentSidebarOpen(false)}
          isEmbedded={true}
          onConfirm={async (buyerCbu, buyerBankName, buyerAccountHolder, orderNotes, paymentMethod) => {
            try {
              const { error } = await supabase
                .from('orders')
                .update({
                  buyer_cbu: buyerCbu,
                  buyer_bank_name: buyerBankName,
                  buyer_account_holder: buyerAccountHolder,
                  notes: orderNotes,
                  payment_method: paymentMethod,
                  status: paymentMethod === 'bank_transfer' ? 'pending' : 'paid',
                  updated_at: new Date().toISOString(),
                })
                .eq('id', order.id);

              if (error) throw error;

              toast({
                title: '✅ Pago registrado',
                description:
                  paymentMethod === 'bank_transfer'
                    ? 'Informaste tu transferencia. El vendedor confirmará el pago.'
                    : 'Pago completado exitosamente.',
              });

              setPaymentSidebarOpen(false);
              setReloadKey((current) => current + 1);
            } catch (err) {
              console.error('Error updating payment:', err);
              toast({
                title: 'Error',
                description: 'No se pudo registrar el pago',
                variant: 'destructive',
              });
            }
          }}
          storeId={order.store_id}
          storeName={store?.name || 'Tienda'}
          total={order.total || 0}
          buyerProfile={{
            bank_accounts: session?.user?.user_metadata?.bank_accounts || [],
          }}
          userId={session?.user?.id || ''}
        />
      )}

      <AddressPickerSidebar
        ref={addressPickerSidebarRef}
        isOpen={!!addressPickerOpen}
        addressType={addressPickerOpen}
        onClose={() => setAddressPickerOpen(null)}
        onSelectAddress={() => setAddressPickerOpen(null)}
        userAddresses={userAddresses}
        sessionUserId={session?.user?.id}
        initialLocation={addressPickerInitialLocation}
        initialDisplayName={addressPickerInitialDisplayName}
        forceAlturaZero={false}
        overlayZIndex={10004}
        panelZIndex={10005}
        fullViewport={true}
      />
    </>
  );
};